/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import axios from 'axios';
import { getUrl } from '../utils/helper';
import { Entry, ENTRY_REVIEW_STATE } from '../types/course/Entry';

export class EntryService {
	public getEntries(courseId: string, token: string): Promise<Entry[]> {
		return axios
			.get<Entry[]>(`${getUrl()}/courses/${courseId}/entries`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getNewEntries(courseId: string, token: string, pageNumber: number): Promise<Entry[]> {
		return axios
			.get<Entry[]>(`${getUrl()}/courses/${courseId}/entries/newEntries/${pageNumber}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getApprovedEntries(courseId: string, token: string, pageNumber: number): Promise<Entry[]> {
		return axios
			.get<Entry[]>(`${getUrl()}/courses/${courseId}/entries/approved/${pageNumber}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getRejectedEntries(courseId: string, token: string, pageNumber: number): Promise<Entry[]> {
		return axios
			.get<Entry[]>(`${getUrl()}/courses/${courseId}/entries/rejected/${pageNumber}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getNumberOfPagesNewEntries(courseId: string, token: string): Promise<number> {
		return axios
			.get<number>(`${getUrl()}/courses/${courseId}/numberOfPages/newEntries`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getNumberOfPagesApproved(courseId: string, token: string): Promise<number> {
		return axios
			.get<number>(`${getUrl()}/courses/${courseId}/numberOfPages/approved`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getNumberOfPagesRejected(courseId: string, token: string): Promise<number> {
		return axios
			.get<number>(`${getUrl()}/courses/${courseId}/numberOfPages/rejected`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public getEntry(entryId: string, token: string): Promise<Entry> {
		return axios
			.get<Entry>(`${getUrl()}/entries/${entryId}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((res) => {
				return res.data;
			});
	}

	public updateReviewState(
		entryId: string,
		newState: ENTRY_REVIEW_STATE,
		token: string,
		feedback?: string,
	): Promise<void> {
		const data = feedback ? { state: newState, feedback } : { state: newState };
		return axios.put(`${getUrl()}/entries/${entryId}/changeState`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	}

	public requestPublication(entryId: string, token: string): Promise<void> {
		return axios.post(`${getUrl()}/entries/${entryId}/requestPublication`, null, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	}
}
